/**
 * Generic, framework-free tree utilities for the Data Room folder hierarchy.
 *
 * These centralize the recursive traversal logic that was previously
 * re-implemented across the slice and components (findFolder / findById /
 * findInTree / findFolderByDetailId / removeFolder).
 *
 * A node only needs a numeric `detailId` and an optional `children` array.
 */
export interface TreeNode {
  detailId: number;
  children?: TreeNode[];
}

/**
 * Depth-first search for the first node matching `detailId`.
 * Returns `undefined` when not found or when `detailId` is null/undefined.
 */
export function findByDetailId<T extends TreeNode>(nodes: T[], detailId: number | null | undefined): T | undefined {
  if (detailId === null || detailId === undefined) return undefined;

  for (const node of nodes) {
    if (node.detailId === detailId) return node;

    const found = findByDetailId((node.children as T[]) || [], detailId);

    if (found) return found;
  }

  return undefined;
}

/**
 * Removes the first node matching `detailId` from the tree, mutating `nodes`
 * in place. Returns `true` when a node was removed.
 */
export function removeByDetailId<T extends TreeNode>(nodes: T[], detailId: number): boolean {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].detailId === detailId) {
      nodes.splice(i, 1);

      return true;
    }

    const children = nodes[i].children as T[] | undefined;

    if (children && children.length > 0 && removeByDetailId(children, detailId)) {
      return true;
    }
  }

  return false;
}
